/**
 * 网页自定义背景图片服务（纯前端，无后端依赖）。
 *
 * 能力：
 * - 支持上传静态图（JPG/PNG/WebP）与 GIF 动态图，分流处理：
 *   - 静态图 >2MB 时用 Canvas 等比缩放到 ≤1920px 并以 0.8 质量压成 JPEG Base64；
 *   - GIF 严格限制 ≤3MB，超限友好拦截；合规则原样读取为 Base64（保留动画，不经 Canvas）。
 * - 三种填充模式：cover / contain / center。contain 模式触发智能边缘融合：
 *   提取首帧四角像素颜色，用 radial-gradient 四角叠加作为底色，图片居中浮于其上。
 * - 大体积 Base64 存入 IndexedDB（localStorage 容量不足）；元数据（模式、融合渐变、
 *   尺寸）走 localStorage，便于启动时同步快速恢复。
 *
 * 调用方从 `@/service/custom_background` 引入，public API 名称稳定。
 */

import { reactive, ref } from 'vue'
import { STORAGE_KEYS } from '@libs/define/storage_key'

export type CustomBackgroundFillMode = 'cover' | 'contain' | 'center'

/** 静态图压缩阈值：超过则走 Canvas 缩放压缩。 */
const STATIC_COMPRESS_THRESHOLD = 2 * 1024 * 1024
/** 静态图压缩后的最大宽度。 */
const STATIC_MAX_WIDTH = 1920
/** 静态图压缩后的最大高度，避免超长图触发浏览器 canvas 尺寸上限。 */
const STATIC_MAX_HEIGHT = 1920
/** 静态图压缩 JPEG 质量。 */
const STATIC_JPEG_QUALITY = 0.8
/** GIF 体积硬上限。 */
const GIF_MAX_SIZE = 3 * 1024 * 1024

const ACCEPTED_MIME = ['image/jpeg', 'image/png', 'image/webp', 'image/gif']

const IDB_NAME = 'driver_custom_background'
const IDB_STORE = 'images'
const IDB_KEY = 'current'
const IDB_VERSION = 1

// ────────────────────────────────────────────────────────────────────────────────
// region: 类型与状态
// ────────────────────────────────────────────────────────────────────────────────

export type CustomBackgroundResult = { ok: true } | { ok: false; reason: 'too_large' | 'unsupported' | 'decode_failed' | 'storage_failed' }

/** 持久化到 localStorage 的轻量元数据（图片 Base64 本身存 IndexedDB）。 */
type CustomBackgroundMeta = {
  version: 1
  enabled: boolean
  fillMode: CustomBackgroundFillMode
  isGif: boolean
  /** contain 模式下由四角颜色合成的 radial-gradient 底色；其余模式为空串。 */
  fusionGradient: string
}

type CustomBackgroundState = CustomBackgroundMeta & {
  /** 运行时图片数据 URL（启动时从 IndexedDB 异步回填）。 */
  dataUrl: string
}

const DEFAULT_META: CustomBackgroundMeta = {
  version: 1,
  enabled: false,
  fillMode: 'cover',
  isGif: false,
  fusionGradient: '',
}

const bgState = reactive<CustomBackgroundState>({
  ...DEFAULT_META,
  dataUrl: '',
})

/** 上传/处理进行中标记，供 UI 显示 loading 与禁用交互。 */
export const customBackgroundBusy = ref(false)

export const customBackground = bgState

// ────────────────────────────────────────────────────────────────────────────────
// region: IndexedDB 封装
// ────────────────────────────────────────────────────────────────────────────────

const idbSupported = (): boolean => typeof indexedDB !== 'undefined'

const openIdb = (): Promise<IDBDatabase> => {
  return new Promise((resolve, reject) => {
    if (!idbSupported()) {
      reject(new Error('indexeddb_unsupported'))
      return
    }

    const request = indexedDB.open(IDB_NAME, IDB_VERSION)
    request.onupgradeneeded = () => {
      const db = request.result
      if (!db.objectStoreNames.contains(IDB_STORE)) {
        db.createObjectStore(IDB_STORE)
      }
    }
    request.onsuccess = () => resolve(request.result)
    request.onerror = () => reject(request.error ?? new Error('indexeddb_open_failed'))
  })
}

const idbPut = async (dataUrl: string): Promise<void> => {
  const db = await openIdb()
  try {
    await new Promise<void>((resolve, reject) => {
      const tx = db.transaction(IDB_STORE, 'readwrite')
      tx.objectStore(IDB_STORE).put(dataUrl, IDB_KEY)
      tx.oncomplete = () => resolve()
      tx.onerror = () => reject(tx.error ?? new Error('indexeddb_put_failed'))
      tx.onabort = () => reject(tx.error ?? new Error('indexeddb_put_aborted'))
    })
  } finally {
    db.close()
  }
}

const idbGet = async (): Promise<string> => {
  const db = await openIdb()
  try {
    return await new Promise<string>((resolve, reject) => {
      const tx = db.transaction(IDB_STORE, 'readonly')
      const req = tx.objectStore(IDB_STORE).get(IDB_KEY)
      req.onsuccess = () => resolve(typeof req.result === 'string' ? req.result : '')
      req.onerror = () => reject(req.error ?? new Error('indexeddb_get_failed'))
    })
  } finally {
    db.close()
  }
}

const idbDelete = async (): Promise<void> => {
  const db = await openIdb()
  try {
    await new Promise<void>((resolve, reject) => {
      const tx = db.transaction(IDB_STORE, 'readwrite')
      tx.objectStore(IDB_STORE).delete(IDB_KEY)
      tx.oncomplete = () => resolve()
      tx.onerror = () => reject(tx.error ?? new Error('indexeddb_delete_failed'))
    })
  } finally {
    db.close()
  }
}

// ────────────────────────────────────────────────────────────────────────────────
// region: 图片处理（读取 / 压缩 / 四角融合）
// ────────────────────────────────────────────────────────────────────────────────

/** 原样读取文件为 Base64 data URL（GIF 走此路径以保留动画）。 */
const readFileAsDataUrl = (file: File): Promise<string> => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.onload = () => resolve(typeof reader.result === 'string' ? reader.result : '')
    reader.onerror = () => reject(reader.error ?? new Error('read_failed'))
    reader.readAsDataURL(file)
  })
}

const loadImage = (src: string): Promise<HTMLImageElement> => {
  return new Promise((resolve, reject) => {
    const img = new Image()
    img.onload = () => resolve(img)
    img.onerror = () => reject(new Error('decode_failed'))
    img.src = src
  })
}

const isRenderableDataUrl = async (dataUrl: string): Promise<boolean> => {
  if (!dataUrl.startsWith('data:image/')) {
    return false
  }

  try {
    await loadImage(dataUrl)
    return true
  } catch (_error) {
    return false
  }
}

/** 静态图：>2MB 时等比缩放到 ≤1920px 宽并压成 0.8 质量 JPEG；否则保持原 dataUrl。 */
const compressStaticImage = async (file: File, originalDataUrl: string): Promise<string> => {
  const img = await loadImage(originalDataUrl)
  const shouldCompress = file.size > STATIC_COMPRESS_THRESHOLD || img.width > STATIC_MAX_WIDTH || img.height > STATIC_MAX_HEIGHT
  if (!shouldCompress) {
    return originalDataUrl
  }

  const scale = Math.min(1, STATIC_MAX_WIDTH / img.width, STATIC_MAX_HEIGHT / img.height)
  const targetW = Math.max(1, Math.round(img.width * scale))
  const targetH = Math.max(1, Math.round(img.height * scale))

  const canvas = document.createElement('canvas')
  canvas.width = targetW
  canvas.height = targetH
  const ctx = canvas.getContext('2d')
  if (!ctx) {
    return originalDataUrl
  }

  ctx.drawImage(img, 0, 0, targetW, targetH)
  const compressedDataUrl = canvas.toDataURL('image/jpeg', STATIC_JPEG_QUALITY)
  return (await isRenderableDataUrl(compressedDataUrl)) ? compressedDataUrl : originalDataUrl
}

/**
 * 智能边缘融合：在小画布上绘制首帧（GIF 仅取第一帧静态图），
 * 采样四个角落像素 RGB，合成四角叠加的 radial-gradient 作为底色。
 */
const buildFusionGradient = async (dataUrl: string): Promise<string> => {
  try {
    const img = await loadImage(dataUrl)
    // 缩到小尺寸采样即可，避免大图绘制开销；GIF 在此只会绘制第一帧。
    const sampleSize = 64
    const canvas = document.createElement('canvas')
    canvas.width = sampleSize
    canvas.height = sampleSize
    const ctx = canvas.getContext('2d', { willReadFrequently: true })
    if (!ctx) {
      return ''
    }

    ctx.drawImage(img, 0, 0, sampleSize, sampleSize)
    const edge = 2
    const far = sampleSize - 1 - edge
    const pickRgb = (x: number, y: number): string => {
      const d = ctx.getImageData(x, y, 1, 1).data
      return `rgb(${d[0]}, ${d[1]}, ${d[2]})`
    }

    const topLeft = pickRgb(edge, edge)
    const topRight = pickRgb(far, edge)
    const bottomLeft = pickRgb(edge, far)
    const bottomRight = pickRgb(far, far)

    // 四角各一个径向渐变叠加，边缘自然过渡到对应角落色。
    return [
      `radial-gradient(circle at 0% 0%, ${topLeft} 0%, transparent 60%)`,
      `radial-gradient(circle at 100% 0%, ${topRight} 0%, transparent 60%)`,
      `radial-gradient(circle at 0% 100%, ${bottomLeft} 0%, transparent 60%)`,
      `radial-gradient(circle at 100% 100%, ${bottomRight} 0%, transparent 60%)`,
    ].join(', ')
  } catch (_error) {
    // 跨域/解码失败不应阻断主流程，融合底色降级为空。
    return ''
  }
}

// ────────────────────────────────────────────────────────────────────────────────
// region: 持久化与 DOM 应用
// ────────────────────────────────────────────────────────────────────────────────

const canUseStorage = (): boolean => typeof window !== 'undefined' && typeof localStorage !== 'undefined'

const persistMeta = () => {
  if (!canUseStorage()) {
    return
  }

  if (!bgState.enabled) {
    localStorage.removeItem(STORAGE_KEYS.CUSTOM_BACKGROUND)
    return
  }

  const meta: CustomBackgroundMeta = {
    version: 1,
    enabled: bgState.enabled,
    fillMode: bgState.fillMode,
    isGif: bgState.isGif,
    fusionGradient: bgState.fusionGradient,
  }
  localStorage.setItem(STORAGE_KEYS.CUSTOM_BACKGROUND, JSON.stringify(meta))
}

/**
 * 将当前状态写入根节点 CSS 变量与标记属性。
 * common.less 中 `[custom-bg='true'] .background-main` 据此渲染背景层。
 */
const applyToDom = () => {
  if (typeof document === 'undefined') {
    return
  }

  const root = document.documentElement

  if (!bgState.enabled || !bgState.dataUrl) {
    root.removeAttribute('custom-bg')
    root.style.removeProperty('--custom-bg-image')
    root.style.removeProperty('--custom-bg-size')
    root.style.removeProperty('--custom-bg-position')
    root.style.removeProperty('--custom-bg-repeat')
    root.style.removeProperty('--custom-bg-fusion')
    return
  }

  const sizeByMode: Record<CustomBackgroundFillMode, string> = {
    cover: 'cover',
    contain: 'contain',
    center: 'auto',
  }

  root.setAttribute('custom-bg', 'true')
  root.setAttribute('custom-bg-mode', bgState.fillMode)
  root.style.setProperty('--custom-bg-image', `url("${bgState.dataUrl}")`)
  root.style.setProperty('--custom-bg-size', sizeByMode[bgState.fillMode])
  root.style.setProperty('--custom-bg-position', 'center')
  root.style.setProperty('--custom-bg-repeat', 'no-repeat')
  // 仅 contain 模式启用四角融合底色；其余模式留空。
  root.style.setProperty('--custom-bg-fusion', bgState.fillMode === 'contain' && bgState.fusionGradient ? bgState.fusionGradient : 'transparent')
}

const sync = () => {
  applyToDom()
  persistMeta()
}

// ────────────────────────────────────────────────────────────────────────────────
// region: Public API
// ────────────────────────────────────────────────────────────────────────────────

let initialized = false

/** 启动时调用：从 localStorage 读元数据，并从 IndexedDB 异步回填图片。 */
export const initializeCustomBackground = () => {
  if (initialized) {
    return
  }
  initialized = true

  if (!canUseStorage()) {
    return
  }

  try {
    const stored = localStorage.getItem(STORAGE_KEYS.CUSTOM_BACKGROUND)
    if (!stored) {
      return
    }

    const meta = JSON.parse(stored) as Partial<CustomBackgroundMeta>
    if (!meta || !meta.enabled) {
      localStorage.removeItem(STORAGE_KEYS.CUSTOM_BACKGROUND)
      return
    }

    bgState.enabled = true
    bgState.fillMode = meta.fillMode === 'contain' || meta.fillMode === 'center' ? meta.fillMode : 'cover'
    bgState.isGif = meta.isGif === true
    bgState.fusionGradient = typeof meta.fusionGradient === 'string' ? meta.fusionGradient : ''
  } catch (_error) {
    localStorage.removeItem(STORAGE_KEYS.CUSTOM_BACKGROUND)
    return
  }

  // 图片数据在 IndexedDB，异步回填；失败则视为无背景并清理元数据。
  idbGet()
    .then((dataUrl) => {
      if (dataUrl) {
        bgState.dataUrl = dataUrl
        applyToDom()
      } else {
        bgState.enabled = false
        persistMeta()
      }
    })
    .catch(() => {
      bgState.enabled = false
      persistMeta()
    })
}

/**
 * 上传并设置自定义背景。返回处理结果（含失败原因），供 UI 提示。
 * - GIF：≤3MB 原样存储（保留动画），超限返回 too_large。
 * - 静态图：>2MB 经 Canvas 缩放压缩为 JPEG。
 */
export const setCustomBackgroundFromFile = async (file: File): Promise<CustomBackgroundResult> => {
  if (!ACCEPTED_MIME.includes(file.type)) {
    return { ok: false, reason: 'unsupported' }
  }

  const isGif = file.type === 'image/gif'

  // GIF 体积硬拦截，置于任何解码/读取之前。
  if (isGif && file.size > GIF_MAX_SIZE) {
    return { ok: false, reason: 'too_large' }
  }

  customBackgroundBusy.value = true
  try {
    const rawDataUrl = await readFileAsDataUrl(file)
    if (!rawDataUrl) {
      return { ok: false, reason: 'decode_failed' }
    }

    // GIF 不经 Canvas 以保留动画；静态图按需压缩。
    const finalDataUrl = isGif ? rawDataUrl : await compressStaticImage(file, rawDataUrl)
    if (!(await isRenderableDataUrl(finalDataUrl))) {
      return { ok: false, reason: 'decode_failed' }
    }

    // 边缘融合底色（contain 模式生效，但提前算好以便切换模式时即时可用）。
    const fusion = await buildFusionGradient(rawDataUrl)

    try {
      await idbPut(finalDataUrl)
    } catch (_error) {
      return { ok: false, reason: 'storage_failed' }
    }

    bgState.dataUrl = finalDataUrl
    bgState.isGif = isGif
    bgState.fusionGradient = fusion
    bgState.enabled = true
    sync()
    return { ok: true }
  } catch (_error) {
    return { ok: false, reason: 'decode_failed' }
  } finally {
    customBackgroundBusy.value = false
  }
}

export const setCustomBackgroundFillMode = (mode: CustomBackgroundFillMode) => {
  bgState.fillMode = mode
  sync()
}

export const setCustomBackgroundEnabled = (enabled: boolean) => {
  bgState.enabled = enabled
  sync()
}

/** 清除背景：移除 IndexedDB 图片、元数据与 DOM 应用。 */
export const clearCustomBackground = async () => {
  bgState.enabled = false
  bgState.dataUrl = ''
  bgState.isGif = false
  bgState.fusionGradient = ''
  bgState.fillMode = 'cover'
  sync()
  try {
    await idbDelete()
  } catch (_error) {
    // 删除失败不影响 UI 状态，下次写入会覆盖。
  }
}
